// Editable sites for the Browser Agent (per browser, localStorage).
// A site owns its login URL AND a list of logins; one login is the default.
// Clicking a site opens its login URL and auto-logs-in with the default login.
//
// SECURITY NOTE: login passwords are stored plaintext in localStorage (prototype).
// The backend never persists them — they're only sent to fill a login form.
// Login URLs below are BEST-GUESS — edit them in the Sites panel.

/** An extra login-form field beyond username/password (e.g. "Agency ID", "PIN").
 *  `label` is matched against the page's input label/placeholder/name/aria-label
 *  server-side to find which box to fill; `value` is what gets typed in. */
export interface LoginExtraField {
  label: string;
  value: string;
}

export interface SiteLogin {
  id: string;
  label: string;
  username: string;
  password: string;
  /** Optional extra fields auto-filled on the login form alongside username/password. */
  extraFields?: LoginExtraField[];
}

export interface AgentSite {
  id: string;
  name: string;
  loginUrl: string;
  logins: SiteLogin[];
  defaultLoginId?: string;
}

const KEY = 'ba_sites_v2';

const DEFAULTS: AgentSite[] = [
  { id: 'momentumamp', name: 'MomentumAMP', loginUrl: 'https://momentumamp.com', logins: [] },
  { id: 'nowcerts', name: 'NowCerts', loginUrl: 'https://nowcerts.com', logins: [] },
  { id: 'applied-epic', name: 'Applied Epic', loginUrl: 'https://www.appliedsystems.com/', logins: [] },
  { id: 'ams360', name: 'AMS360', loginUrl: 'https://www.ams360.com/', logins: [] },
  { id: 'vertafore-platform', name: 'Vertafore Platform', loginUrl: 'https://login.vertafore.com/', logins: [] },
  { id: 'hawksoft', name: 'HawkSoft', loginUrl: 'https://www.hawksoft.com/', logins: [] },
  { id: 'ezlynx', name: 'EZLynx', loginUrl: 'https://app.ezlynx.com/', logins: [] },
  { id: 'agencybloc', name: 'AgencyBloc', loginUrl: 'https://app.agencybloc.com/', logins: [] },
];

function notify(): void {
  try { window.dispatchEvent(new CustomEvent('ba:sites-changed')); } catch { /* ignore */ }
}

function read(): AgentSite[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) {
      localStorage.setItem(KEY, JSON.stringify(DEFAULTS));
      return DEFAULTS;
    }
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return DEFAULTS;
    // Auto-heal earlier seeded URLs that don't resolve (only if left unchanged).
    const FIX: Record<string, string> = {
      'https://app.nowcerts.com': 'https://nowcerts.com',
      'https://app.momentumamp.com': 'https://momentumamp.com',
    };
    let changed = false;
    const sites = arr.map((s: any) => {
      const key = (s.loginUrl || '').replace(/\/$/, '');
      const loginUrl = FIX[key] ?? s.loginUrl;
      if (loginUrl !== s.loginUrl) changed = true;
      return {
        id: s.id, name: s.name, loginUrl,
        logins: Array.isArray(s.logins) ? s.logins : [],
        defaultLoginId: s.defaultLoginId,
      };
    });
    if (changed) {
      try { localStorage.setItem(KEY, JSON.stringify(sites)); } catch { /* ignore */ }
    }
    return sites;
  } catch {
    return DEFAULTS;
  }
}

function write(sites: AgentSite[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(sites)); } catch { /* ignore */ }
  notify();
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function hostOf(url: string): string {
  try { return new URL(url).host.replace(/^www\./, '').toLowerCase(); } catch { return ''; }
}

export function listSites(): AgentSite[] {
  return read();
}

export function getSite(siteId: string): AgentSite | undefined {
  return read().find((s) => s.id === siteId);
}

export function findSiteByDomain(domain: string): AgentSite | undefined {
  const d = (domain || '').replace(/^www\./, '').toLowerCase();
  if (!d) return undefined;
  return read().find((s) => {
    const h = hostOf(s.loginUrl);
    return h && (h === d || h.endsWith('.' + d) || d.endsWith('.' + h));
  });
}

/** The default login for a site (explicit default, else the first login). */
export function getDefaultLogin(site: AgentSite | undefined): SiteLogin | undefined {
  if (!site || site.logins.length === 0) return undefined;
  return site.logins.find((l) => l.id === site.defaultLoginId) ?? site.logins[0];
}

/** Add (no id) or update (with id) a site's name/loginUrl. */
export function saveSite(site: { id?: string; name: string; loginUrl: string }): AgentSite {
  const sites = read();
  if (site.id) {
    const next = sites.map((s) => (s.id === site.id ? { ...s, name: site.name, loginUrl: site.loginUrl } : s));
    write(next);
    return next.find((s) => s.id === site.id)!;
  }
  const created: AgentSite = { id: newId('site'), name: site.name, loginUrl: site.loginUrl, logins: [] };
  write([...sites, created]);
  return created;
}

export function deleteSite(id: string): void {
  write(read().filter((s) => s.id !== id));
}

function cleanExtras(extras?: LoginExtraField[]): LoginExtraField[] | undefined {
  const cleaned = (extras || [])
    .map((f) => ({ label: (f.label || '').trim(), value: f.value ?? '' }))
    .filter((f) => f.label);  // drop rows with no label (nothing to match against)
  return cleaned.length ? cleaned : undefined;
}

export function addLogin(siteId: string, login: { label: string; username: string; password: string; extraFields?: LoginExtraField[] }): void {
  const sites = read();
  const next = sites.map((s) => {
    if (s.id !== siteId) return s;
    const l: SiteLogin = {
      id: newId('login'), label: login.label || login.username,
      username: login.username, password: login.password,
      extraFields: cleanExtras(login.extraFields),
    };
    const logins = [...s.logins, l];
    return { ...s, logins, defaultLoginId: s.defaultLoginId ?? l.id };  // first login becomes default
  });
  write(next);
}

export function updateLogin(siteId: string, loginId: string, fields: Partial<Pick<SiteLogin, 'label' | 'username' | 'password' | 'extraFields'>>): void {
  const next = read().map((s) =>
    s.id !== siteId ? s : {
      ...s,
      logins: s.logins.map((l) =>
        l.id === loginId
          ? {
              ...l, ...fields,
              label: (fields.label ?? l.label) || (fields.username ?? l.username),
              extraFields: 'extraFields' in fields ? cleanExtras(fields.extraFields) : l.extraFields,
            }
          : l
      ),
    }
  );
  write(next);
}

export function deleteLogin(siteId: string, loginId: string): void {
  const next = read().map((s) => {
    if (s.id !== siteId) return s;
    const logins = s.logins.filter((l) => l.id !== loginId);
    const defaultLoginId = s.defaultLoginId === loginId ? logins[0]?.id : s.defaultLoginId;
    return { ...s, logins, defaultLoginId };
  });
  write(next);
}

export function setDefaultLogin(siteId: string, loginId: string): void {
  write(read().map((s) => (s.id === siteId ? { ...s, defaultLoginId: loginId } : s)));
}
